import React from 'react';
import PropTypes from 'prop-types';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import SearchableDropdown, {
  SEARCHABLE_DROPDOWN_VARIANT,
} from '../../../components/SearchableDropdown';
import WaeStandardTextField from '../../../components/WaeStandardTextField/WaeStandardTextField';
import { BLACK } from '../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../theme/fontConstants';
import SectionCard from '../../positionTemplate/components/SectionCard';
import corporationOptionsCallback from '../api/corporationOptions';
import FormFieldRow from '../components/FormFieldRow';
import { textFieldSx } from '../components/formFieldStyles';
import SelectField from '../components/SelectField';
import {
  DESCRIPTION_MAX_LENGTH,
  getSectionAnchorId,
  INDUSTRY_OPTIONS,
  POSITION_NAME_MAX_LENGTH,
  SECTION_IDS,
} from '../constants';

const NAME_INPUT_ID = 'position-template-name';
const INDUSTRY_INPUT_ID = 'position-template-industry';
const DESCRIPTION_INPUT_ID = 'position-template-description';

const getCorporationLabel = (option) => option?.name || '';

const PositionDetailsSection = ({ values, setField }) => {
  const title = LanguageConverterV2('positionTemplate.create.positionDetails.title');
  const corporationPlaceholder = LanguageConverterV2(
    'positionTemplate.create.positionDetails.corporation.placeholder'
  );
  const industryLabel = LanguageConverterV2(
    'positionTemplate.create.positionDetails.industry.label'
  );
  const industryPlaceholder = LanguageConverterV2(
    'positionTemplate.create.positionDetails.industry.placeholder'
  );

  return (
    <Box
      id={getSectionAnchorId(SECTION_IDS.POSITION_DETAILS)}
      data-testid="position-details-section"
      sx={{ scrollMarginTop: '16px' }}
    >
      <SectionCard
        header={
          <Typography sx={{ color: BLACK[100], fontFamily: PRIMARY_FONT[700], fontSize: '14px' }}>
            {title}
          </Typography>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '8px 0' }}>
          <FormFieldRow
            labelKey="positionTemplate.create.positionDetails.corporation.label"
            helperKey="positionTemplate.create.positionDetails.corporation.helper"
            required
          >
            <SearchableDropdown
              value={values.corporation}
              onChange={(event, corporation) => {
                setField('corporation', corporation);
                if (corporation?._id !== values.corporation?._id) {
                  setField('locations', []);
                }
              }}
              optionsAPICallback={corporationOptionsCallback}
              getOptionLabel={getCorporationLabel}
              placeholder={corporationPlaceholder}
              variant={SEARCHABLE_DROPDOWN_VARIANT.FORM}
              testId="position-template-corporation"
            />
          </FormFieldRow>
          <FormFieldRow
            labelKey="positionTemplate.create.positionDetails.name.label"
            helperKey="positionTemplate.create.positionDetails.name.helper"
            htmlFor={NAME_INPUT_ID}
            required
          >
            <WaeStandardTextField
              id={NAME_INPUT_ID}
              fullWidth
              value={values.name}
              onChange={(event) => setField('name', event.target.value)}
              inputProps={{ maxLength: POSITION_NAME_MAX_LENGTH }}
              sx={textFieldSx}
            />
          </FormFieldRow>
          <FormFieldRow labelKey="positionTemplate.create.positionDetails.industry.label" required>
            <SelectField
              id={INDUSTRY_INPUT_ID}
              value={values.industry ?? ''}
              options={INDUSTRY_OPTIONS}
              onChange={(industry) => setField('industry', industry)}
              ariaLabel={industryLabel}
              placeholder={industryPlaceholder}
              testId={INDUSTRY_INPUT_ID}
            />
          </FormFieldRow>
          <FormFieldRow
            labelKey="positionTemplate.create.positionDetails.description.label"
            htmlFor={DESCRIPTION_INPUT_ID}
            required
          >
            <WaeStandardTextField
              id={DESCRIPTION_INPUT_ID}
              fullWidth
              multiline
              minRows={4}
              value={values.description}
              onChange={(event) => setField('description', event.target.value)}
              inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH }}
              sx={textFieldSx}
            />
          </FormFieldRow>
        </Box>
      </SectionCard>
    </Box>
  );
};

PositionDetailsSection.propTypes = {
  values: PropTypes.shape({
    corporation: PropTypes.shape({ _id: PropTypes.string, name: PropTypes.string }),
    name: PropTypes.string,
    industry: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
  setField: PropTypes.func.isRequired,
};

export default PositionDetailsSection;
